import { edgeInvoke } from '@/lib/edgeInvoke';

export async function getThumbnailUrl(playbackId: string, playbackPolicy?: string | null): Promise<string> {
  try {
    if (import.meta.env.VITE_DEBUG_THUMBS === 'true') {
      console.log(`Getting thumbnail for playback ID: ${playbackId}, policy: ${playbackPolicy}`);
    }
    
    // Handle missing playbackId (videos still processing)
    if (!playbackId) {
      throw new Error('PlaybackId is required for thumbnail generation');
    }
    
    // HOTFIX: Handle build-time and missing policies gracefully
    const resolvedPolicy = playbackPolicy || 'signed'; // Default to signed
    
    // SECURITY: All videos must use signed URLs - no public URLs allowed
    if (resolvedPolicy !== 'signed') {
      console.error('Invalid playback policy - only signed videos allowed');
      throw new Error(`All videos must use signed playback policy, got: ${resolvedPolicy}`);
    }
    
    // For signed videos, get signed URL from edge function
    if (resolvedPolicy === 'signed') {
      if (import.meta.env.VITE_DEBUG_THUMBS === 'true') {
        console.log('Getting signed thumbnail URL... [v005-CONSISTENT-ARCH]');
      }
      
      // Use same edgeInvoke('playback-url') pattern as videos (CONSISTENT ARCHITECTURE)
      const response = await edgeInvoke('playback-url', {
        playback_id: playbackId,
        type: 'thumbnail',
        time: 1 // Get frame at 1 second
      });

      if (response.error) {
        console.error('Signed thumbnail request failed:', response.error);
        throw new Error(`Signed thumbnail failed: ${response.error}`);
      }

      if (response.url) {
        if (import.meta.env.VITE_DEBUG_THUMBS === 'true') {
          console.log('Signed thumbnail result:', { url: response.url.substring(0, 100) + '...' });
        }
        return response.url;
      }
      
      // SECURITY: No public fallback for signed videos
      console.error('Signed thumbnail generation failed - no fallback allowed');
      throw new Error('Failed to generate signed thumbnail URL');
    }
    
    // SECURITY: All videos must be signed - no unknown policies allowed
    console.error('Unknown playback policy - all videos must be signed');
    throw new Error(`Invalid playback policy: ${resolvedPolicy}`);
    
  } catch (error) {
    console.error('Failed to get thumbnail URL:', error);
    // SECURITY: No unsigned thumbnail fallbacks allowed
    throw error;
  }
}

// Legacy function for backward compatibility
export async function getSignedThumbnailUrl(playbackId: string): Promise<string> {
  return getThumbnailUrl(playbackId, 'signed');
}